import { useRef, useState, useCallback } from "react";
import { Video, X, Upload, RefreshCw, Play, Camera, Scissors } from "lucide-react";
import { OnboardingVideo } from "@/hooks/useOnboarding";
import { toast } from "@/hooks/use-toast";
import CameraVideoRecorder from "./CameraVideoRecorder";
import VideoTrimModal from "./VideoTrimModal";

interface OnboardingVideoUploadProps {
  video: OnboardingVideo | null;
  onSetVideo: (file: File, duration: number) => void;
  onRemoveVideo: () => void;
  isRequired: boolean;
}

const OnboardingVideoUpload = ({
  video,
  onSetVideo,
  onRemoveVideo,
  isRequired,
}: OnboardingVideoUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  
  // Trim modal state
  const [showTrimModal, setShowTrimModal] = useState(false);
  const [pendingVideoSrc, setPendingVideoSrc] = useState<string | null>(null);
  const [pendingVideoDuration, setPendingVideoDuration] = useState(0);

  const validateVideo = useCallback(
    async (file: File): Promise<{ valid: boolean; duration: number }> => {
      return new Promise((resolve) => {
        const videoEl = document.createElement("video");
        videoEl.preload = "metadata";

        videoEl.onloadedmetadata = () => {
          const duration = videoEl.duration;
          URL.revokeObjectURL(videoEl.src);
          resolve({ valid: true, duration });
        };

        videoEl.onerror = () => {
          URL.revokeObjectURL(videoEl.src);
          resolve({ valid: false, duration: 0 });
        };

        videoEl.src = URL.createObjectURL(file);
      });
    },
    []
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";

      setValidating(true);

      const { valid, duration } = await validateVideo(file);

      if (!valid) {
        toast({
          title: "Invalid video",
          description: "Could not process this video file",
          variant: "destructive",
        });
        setValidating(false);
        return;
      }

      // If video is within range, use directly
      if (duration >= 5 && duration <= 15) {
        onSetVideo(file, duration);
        setValidating(false);
        return;
      }

      // If video is too short, reject
      if (duration < 5) {
        toast({
          title: "Video too short",
          description: "Video must be at least 5 seconds long",
          variant: "destructive",
        });
        setValidating(false);
        return;
      }

      // If video is too long, open trim modal
      if (duration > 15) {
        const url = URL.createObjectURL(file);
        setPendingVideoSrc(url);
        setPendingVideoDuration(duration);
        setShowTrimModal(true);
        setValidating(false);
      }
    },
    [validateVideo, onSetVideo]
  );

  const handleTrimComplete = useCallback(
    (trimmedBlob: Blob, duration: number) => {
      const file = new File([trimmedBlob], `intro-video-${Date.now()}.webm`, {
        type: trimmedBlob.type,
      });
      onSetVideo(file, duration);
      
      // Cleanup
      if (pendingVideoSrc) {
        URL.revokeObjectURL(pendingVideoSrc);
      }
      setPendingVideoSrc(null);
      setShowTrimModal(false);
    },
    [onSetVideo, pendingVideoSrc]
  );

  const handleCloseTrimModal = useCallback(() => {
    if (pendingVideoSrc) {
      URL.revokeObjectURL(pendingVideoSrc);
    }
    setPendingVideoSrc(null);
    setShowTrimModal(false);
  }, [pendingVideoSrc]);

  const handleVideoRecorded = useCallback(
    (file: File, duration: number) => {
      onSetVideo(file, duration);
      setShowCamera(false);
    },
    [onSetVideo]
  );

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          Intro Video
          {isRequired ? (
            <span className="text-destructive ml-1">*</span>
          ) : (
            <span className="text-muted-foreground text-sm font-normal ml-2">
              (Optional)
            </span>
          )}
        </h3>
      </div>

      <p className="text-sm text-muted-foreground">
        {isRequired
          ? "Record or upload a 5-15 second selfie-style video. Longer videos can be trimmed."
          : "Add a video to stand out! Longer videos can be trimmed to 15 seconds."}
      </p>

      {video ? (
        // Video preview
        <div className="relative aspect-[9/16] max-w-[200px] rounded-2xl overflow-hidden bg-black">
          <video
            ref={videoRef}
            src={video.preview}
            className="w-full h-full object-cover"
            playsInline
            onEnded={handleVideoEnded}
          />

          {/* Play/Pause overlay */}
          <button
            onClick={togglePlayPause}
            className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
          >
            {!isPlaying && (
              <div className="p-3 rounded-full bg-white/30 backdrop-blur-sm">
                <Play className="w-6 h-6 text-white fill-white" />
              </div>
            )}
          </button>

          {/* Duration badge */}
          <div className="absolute bottom-2 left-2 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
            {Math.round(video.duration)}s
          </div>

          {/* Actions */}
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              onClick={() => setShowCamera(true)}
              className="p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
              title="Re-record video"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
              title="Upload different video"
            >
              <RefreshCw className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={onRemoveVideo}
              className="p-2 rounded-full bg-destructive/80 backdrop-blur-sm hover:bg-destructive transition-colors"
              title="Remove video"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      ) : (
        // Upload/Record options
        <div className="flex gap-3">
          {/* Record button */}
          <button
            onClick={() => setShowCamera(true)}
            disabled={validating}
            className="flex-1 aspect-[9/16] max-w-[140px] rounded-2xl border-2 border-dashed border-pop bg-pop/5 hover:bg-pop/10 transition-colors flex flex-col items-center justify-center gap-2 text-pop disabled:opacity-50"
          >
            <Camera className="w-8 h-8" />
            <div className="text-center">
              <span className="text-sm font-medium block">Record</span>
              <span className="text-xs opacity-70">Use camera</span>
            </div>
          </button>

          {/* Upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={validating}
            className="flex-1 aspect-[9/16] max-w-[140px] rounded-2xl border-2 border-dashed border-border hover:border-pop transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-pop disabled:opacity-50"
          >
            {validating ? (
              <>
                <div className="w-8 h-8 rounded-full border-2 border-current border-t-transparent animate-spin" />
                <span className="text-sm">Processing...</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8" />
                <div className="text-center">
                  <span className="text-sm font-medium block">Upload</span>
                  <span className="text-xs opacity-70">Auto-trim if needed</span>
                </div>
                <Scissors className="w-4 h-4 opacity-50" />
              </>
            )}
          </button>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Vertical format, 5-15 seconds, selfie-style
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {isRequired && !video && (
        <p className="text-sm text-destructive">A short video is required to continue</p>
      )}

      {/* Camera Recorder Modal */}
      <CameraVideoRecorder
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onVideoRecorded={handleVideoRecorded}
        minDuration={5}
        maxDuration={15}
      />

      {/* Video Trim Modal */}
      {pendingVideoSrc && (
        <VideoTrimModal
          isOpen={showTrimModal}
          videoSrc={pendingVideoSrc}
          videoDuration={pendingVideoDuration}
          onClose={handleCloseTrimModal}
          onTrimComplete={handleTrimComplete}
          minDuration={5}
          maxDuration={15}
        />
      )}
    </div>
  );
};

export default OnboardingVideoUpload;
